// File: /var/www/mt5-manager/frontend/src/components/MT5/MT5HistoryIcon.jsx

import React, { useState, useEffect, useCallback } from 'react';
import config from '../../config';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer 
} from 'recharts';

const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeCurve = (curve) => {
  if (!Array.isArray(curve) || curve.length === 0) return [];
  return curve
    .map((point) => {
      const dateRaw = point?.date || point?.time || point?.label || point?.x;
      if (!dateRaw) return null;
      const parsed = new Date(dateRaw);
      const date = Number.isNaN(parsed.getTime())
        ? String(dateRaw).slice(0, 10)
        : parsed.toISOString().slice(0, 10);
      const balance = toFiniteNumber(point?.balance, 0);
      const equity = toFiniteNumber(point?.equity, balance);
      return { date, balance, equity };
    })
    .filter(Boolean);
};

const buildCurveFromTrades = (trades, currentBalance) => {
  const rows = Array.isArray(trades) ? [...trades] : [];
  const dated = rows
    .map((trade) => {
      const rawDate = trade?.close_time || trade?.open_time;
      if (!rawDate) return null;
      const parsed = new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return null;
      return {
        date: parsed.toISOString().slice(0, 10),
        profit: toFiniteNumber(trade?.profit, 0)
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.date.localeCompare(b.date));

  const balanceNow = toFiniteNumber(currentBalance, 0);
  if (dated.length === 0) {
    return [{
      date: new Date().toISOString().slice(0, 10),
      balance: balanceNow,
      equity: balanceNow
    }];
  }

  const totalProfit = dated.reduce((sum, item) => sum + item.profit, 0);
  const startingBalance = balanceNow - totalProfit;
  const profitByDate = new Map();
  for (const item of dated) {
    profitByDate.set(item.date, (profitByDate.get(item.date) || 0) + item.profit);
  }

  let runningBalance = startingBalance;
  const curve = [];
  for (const [date, dayProfit] of profitByDate.entries()) {
    runningBalance += dayProfit;
    curve.push({
      date,
      balance: runningBalance,
      equity: runningBalance
    });
  }
  return curve;
};

const computeFloatingFromPositions = (positions) => {
  if (!Array.isArray(positions) || positions.length === 0) return 0;
  return positions.reduce((sum, pos) => {
    const profit = toFiniteNumber(pos?.profit, 0);
    const swap = toFiniteNumber(pos?.swap, 0);
    return sum + profit + swap;
  }, 0);
};

const MT5HistoryIcon = ({ account, isModal = false, onCashflow, hideAccountNumber = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [journalData, setJournalData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [journalSortOrder, setJournalSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [activePreset, setActivePreset] = useState('Last 30 days');
  
  // Date range state for filtering
  const [dateRange, setDateRange] = useState({
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30); // Default to last 30 days
      return date.toISOString().split('T')[0];
    })(),
    endDate: new Date().toISOString().split('T')[0]
  });



  const computeCashflowFromDeals = useCallback((deals) => {
    const arr = Array.isArray(deals) ? deals : [];
    let deposits = 0;
    let withdrawals = 0;
    const depositEntries = [];
    const withdrawalEntries = [];

    for (const d of arr) {
      if (d?.type_value === 2 || d?.type === 'Balance') {
        const amt = Number(d?.profit || 0);
        let tsSec;
        if (typeof d?.time_msc === 'number' && !Number.isNaN(d.time_msc)) {
          tsSec = Math.floor(d.time_msc / 1000);
        } else if (typeof d?.time === 'number' && !Number.isNaN(d.time)) {
          tsSec = d.time;
        }
        if (amt > 0) {
          deposits += amt;
          if (tsSec) depositEntries.push({ amount: amt, time: tsSec });
        } else if (amt < 0) {
          withdrawals += amt; // negative
          if (tsSec) withdrawalEntries.push({ amount: amt, time: tsSec });
        }
      }
    }

    depositEntries.sort((a, b) => a.time - b.time);
    withdrawalEntries.sort((a, b) => a.time - b.time);

    const lastDeposit = depositEntries.length ? depositEntries[depositEntries.length - 1] : null;
    const lastWithdrawal = withdrawalEntries.length ? withdrawalEntries[withdrawalEntries.length - 1] : null;

    return { deposits, withdrawals, lastDeposit, lastWithdrawal, depositEntries, withdrawalEntries };
  }, []);

  const fetchCashflow12m = useCallback(async () => {
    if (typeof onCashflow !== 'function') return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const now = new Date();
    const start = new Date();
    start.setFullYear(now.getFullYear() - 1);
    const days = Math.max(1, Math.ceil((now - start) / (1000 * 3600 * 24)));

    const response = await fetch(`${config.apiUrl}/ea/history`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        mt5_account: account.login || account.account_number,
        days,
        from_date: start.toISOString(),
        to_date: now.toISOString()
      })
    });

    if (!response.ok) return;

    const data = await response.json();
    const deals = Array.isArray(data?.deals) ? data.deals : [];
    const cashflow = computeCashflowFromDeals(deals);
    onCashflow(cashflow);
  }, [account, computeCashflowFromDeals, onCashflow]);

  const fetchJournalData = useCallback(async (customStartDate = null, customEndDate = null, expandRange = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Use custom dates if provided, otherwise use state dates
      const startDate = customStartDate ? new Date(customStartDate) : new Date(dateRange.startDate);
      const endDate = customEndDate ? new Date(customEndDate) : new Date(dateRange.endDate);
      
      // Calculate days difference for API compatibility
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      const response = await fetch(`${config.apiUrl}/ea/history`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mt5_account: account.login || account.account_number,
          days: Math.max(daysDiff, 1),
          from_date: startDate.toISOString(),
          to_date: endDate.toISOString(),
          expand_range: expandRange
        })
      });

      if (!response.ok) {
        const retryAfter = response.headers.get('Retry-After');
        let detail = `${response.status} ${response.statusText}`;
        try {
          const err = await response.json();
          if (err && typeof err === 'object') {
            detail = err.detail || err.message || JSON.stringify(err);
          }
        } catch (e) {
          try {
            const txt = await response.text();
            if (txt) detail = txt;
          } catch (_) {
            // ignore
          }
        }

        if (response.status === 503) {
          const waitMsg = retryAfter ? ` Please retry in ${retryAfter}s.` : ' Please retry shortly.';
          throw new Error(`MT5 is busy or temporarily unavailable.${waitMsg} (${detail})`);
        }
        throw new Error(`Failed to fetch trading history: ${detail}`);
      }

      const data = await response.json();
      
      // Debug: Log the received data structure
      console.log('=== MT5 History Data Received ===');
      console.log('Full data:', data);
      console.log('Summary:', data.summary);
      console.log('Monthly performance:', data.monthly_performance);
      console.log('Pair performance:', data.pair_performance);
      console.log('Trades/Deals:', data.trades || data.deals);

      const trades = Array.isArray(data.trades) ? data.trades : (Array.isArray(data.deals) ? data.deals : []);
      const normalizedAccountId = String(account.login || account.account_number || '').trim();
      const accountBalance = data.account_info?.balance ?? account?.balance ?? 0;
      const accountEquity = data.account_info?.equity ?? account?.equity ?? accountBalance;

      let floatingDelta = 0;
      try {
        const positionsResponse = await fetch(
          `${config.apiUrl}/ea/positions?mt5_account=${encodeURIComponent(normalizedAccountId)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (positionsResponse.ok) {
          const positionsData = await positionsResponse.json();
          floatingDelta = computeFloatingFromPositions(positionsData);
        } else {
          floatingDelta = toFiniteNumber(accountEquity, accountBalance) - toFiniteNumber(accountBalance, 0);
        }
      } catch (_positionsErr) {
        floatingDelta = toFiniteNumber(accountEquity, accountBalance) - toFiniteNumber(accountBalance, 0);
      }

      // ---------------------------------------------------------------
      // Build the chart curve.
      // When the backend injected real hourly snapshots it sets
      // _has_snapshot_curves = true AND each point already has BOTH
      // 'balance' and 'equity' fields containing the historically
      // observed values. In that case we use the data as-is without any
      // equity-rebuild logic (which would incorrectly spread the current
      // floating P&L across ALL historical dates).
      // ---------------------------------------------------------------
      const hasSnapshotCurves = !!data._has_snapshot_curves;
      const providedBalanceCurve = normalizeCurve(data.balance_curve);
      const providedEquityCurve = normalizeCurve(data.equity_curve);

      let chartCurve;

      if (hasSnapshotCurves && providedBalanceCurve.length > 0) {
        // Snapshot data: balance AND equity are real observed values per day.
        // Just ensure both fields exist on every point (equity falls back to
        // balance+floatingDelta only for the LAST point if equity === balance,
        // i.e. account was flat at snapshot time but has open positions now).
        chartCurve = providedBalanceCurve.map((point, idx) => {
          const balance = toFiniteNumber(point.balance, 0);
          let equity = toFiniteNumber(point.equity, balance);
          // If the snapshot equity equals balance (account was flat at that time)
          // AND this is the most recent data point, show live floating P&L.
          if (idx === providedBalanceCurve.length - 1 && Math.abs(equity - balance) < 0.0001) {
            equity = balance + floatingDelta;
          }
          return { date: point.date, balance, equity };
        });
      } else {
        // No snapshots – fall back to trade-derived curves.
        // If positions endpoint returns no floating data, try account summary floating_pl as fallback.
        if (Math.abs(floatingDelta) < 0.0001) {
          try {
            const summaryResponse = await fetch(
              `${config.apiUrl}/account/summary?mt5_account=${encodeURIComponent(normalizedAccountId)}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            if (summaryResponse.ok) {
              const summaryData = await summaryResponse.json();
              const summaryFloating = toFiniteNumber(summaryData?.floating_pl, 0);
              if (Math.abs(summaryFloating) > 0.0001) {
                floatingDelta = summaryFloating;
              }
            }
          } catch (_summaryErr) {
            // Ignore summary fallback errors and keep current floatingDelta.
          }
        }

        const fallbackCurve = buildCurveFromTrades(trades, accountBalance);
        const mergedCurve = providedBalanceCurve.length > 0 ? providedBalanceCurve : fallbackCurve;
        const providedEquityLooksFlatToBalance =
          providedEquityCurve.length > 0 &&
          providedBalanceCurve.length > 0 &&
          providedEquityCurve.every((eqPoint) => {
            const match = providedBalanceCurve.find((b) => b.date === eqPoint.date);
            if (!match) return false;
            return Math.abs(toFiniteNumber(eqPoint.equity, 0) - toFiniteNumber(match.balance, 0)) < 0.0001;
          });
        const shouldRebuildEquityCurve = providedEquityCurve.length === 0 || providedEquityLooksFlatToBalance;
        const mergedEquity = !shouldRebuildEquityCurve
          ? providedEquityCurve
          : mergedCurve.map((point) => {
              const baseBalance = toFiniteNumber(point.balance, 0);
              return {
                ...point,
                equity: baseBalance + floatingDelta
              };
            });

        const curveByDate = new Map();
        for (const point of mergedCurve) {
          curveByDate.set(point.date, {
            date: point.date,
            balance: toFiniteNumber(point.balance, 0),
            equity: toFiniteNumber(point.equity, point.balance)
          });
        }
        for (const point of mergedEquity) {
          const existing = curveByDate.get(point.date) || {
            date: point.date,
            balance: toFiniteNumber(point.balance, 0),
            equity: toFiniteNumber(point.equity, point.balance)
          };
          existing.equity = toFiniteNumber(point.equity, existing.balance);
          if (!Number.isFinite(existing.balance)) {
            existing.balance = toFiniteNumber(point.balance, 0);
          }
          curveByDate.set(point.date, existing);
        }
        const sortedCurve = Array.from(curveByDate.values()).sort((a, b) => a.date.localeCompare(b.date));
        chartCurve = (shouldRebuildEquityCurve && sortedCurve.length > 0)
          ? sortedCurve.map((point) => ({
              ...point,
              equity: toFiniteNumber(point.balance, 0) + floatingDelta
            }))
          : sortedCurve;
      }

      // Set both dashboard and journal data from the MT5 service response
      const tradeProfit     = trades.reduce((s, t) => s + toFiniteNumber(t.profit, 0), 0);
      const tradeCommission = trades.reduce((s, t) => s + toFiniteNumber(t.commission, 0), 0);
      const tradeSwap       = trades.reduce((s, t) => s + toFiniteNumber(t.swap, 0), 0);

      setDashboardData({
        total_profit: tradeProfit + tradeCommission + tradeSwap,
        win_rate: data.summary?.win_rate || 0,
        total_trades: data.summary?.total_trades || 0,
        average_profit: data.summary?.average_profit || 0,
        monthly_performance: data.monthly_performance || [],
        pair_performance: data.pair_performance || [],
        balance_curve: chartCurve,
        equity_curve: chartCurve,
        long_profit: data.summary?.long_profit || 0,
        short_profit: data.summary?.short_profit || 0,
        winning_trades: data.summary?.winning_trades || 0,
        losing_trades: data.summary?.losing_trades || 0,
        profit_factor: data.summary?.profit_factor || 0,
        largest_win: data.summary?.largest_win || 0,
        largest_loss: data.summary?.largest_loss || 0,        average_win: data.summary?.average_win || 0,
        average_loss: data.summary?.average_loss || 0,
        max_consecutive_wins: data.summary?.max_win_streak || 0,
        max_consecutive_losses: data.summary?.max_loss_streak || 0,
        max_drawdown: data.summary?.max_drawdown || 0,
        max_drawdown_pct: data.summary?.max_drawdown_pct || 0,
        current_balance: data.account_info?.balance || 10000,
        starting_capital: 10000,
        gross_profit: data.summary?.gross_profit || 0,
        gross_loss: data.summary?.gross_loss || 0,
        total_commission: tradeCommission,
        total_swap: tradeSwap,
        profit_per_trade: data.summary?.profit_per_trade || data.algorithm_metrics?.profit_per_trade || 0,
        expectancy: data.summary?.expectancy || data.summary?.profit_per_trade || data.summary?.average_profit || 0,
        sharpe_ratio: data.summary?.sharpe_ratio || 1.2,
        calmar_ratio: data.summary?.calmar_ratio || 0.8,
        average_holding_time: data.summary?.average_holding_time || data.algorithm_metrics?.average_holding_time || '00:00:00',
        recovery_factor: data.summary?.recovery_factor || Math.abs((data.summary?.net_profit || 0) / (data.summary?.max_drawdown || 1))
      });

      setJournalData({
        trades,
        deals: Array.isArray(data.deals) ? data.deals : [],
      });
        setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [account, dateRange]);const openModal = useCallback(async () => {
    setIsModalOpen(true);
    // Optional: compute cashflow only when History is opened (keeps Affiliate page from auto-fetching history)
    if (typeof onCashflow === 'function') {
      fetchCashflow12m().catch(() => {});
    }
    await fetchJournalData();
  }, [fetchCashflow12m, fetchJournalData, onCashflow]);

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };
  // Auto-fetch data when component mounts if in modal mode
  useEffect(() => {
    if (isModal) {
      openModal();
    }
  }, [isModal, openModal]);

  // Auto-fetch cashflow in the background when this account card renders
  // so the affiliate tab shows Deposits/Withdrawals without requiring a click.
  // Stagger by a random 0-8s delay to avoid all accounts firing simultaneously.
  useEffect(() => {
    if (typeof onCashflow !== 'function') return;
    const delay = Math.floor(Math.random() * 8000);
    const timer = setTimeout(() => {
      fetchCashflow12m().catch(() => {});
    }, delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* History Icon Button - only show if not in modal mode */}
      {!isModal && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering parent click events
            openModal();
          }}
          className="ml-2 p-1 text-gray-600 hover:text-blue-500 focus:outline-none"
          title="View Trading History"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </button>
      )}      {/* Modal for Trading History - show if modal is open OR if in modal mode */}
      {(isModalOpen || isModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" onClick={closeModal}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all my-8 align-middle max-w-7xl w-full mx-auto" style={{width: '95vw', maxWidth: '1400px'}}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Trading History{hideAccountNumber ? '' : ` - Account ${account.account_number}`}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>                {/* Tab Navigation */}
                <div className="border-b mb-4">
                  <nav className="flex -mb-px">
                    <button
                      className={`py-2 px-4 font-medium text-sm ${
                        activeTab === 'dashboard'
                          ? 'border-b-2 border-blue-500 text-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => setActiveTab('dashboard')}
                    >
                      Dashboard
                    </button>
                    <button
                      className={`py-2 px-4 font-medium text-sm ${
                        activeTab === 'journal'
                          ? 'border-b-2 border-blue-500 text-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => setActiveTab('journal')}
                    >
                      Full Journal
                    </button>
                  </nav>
                </div>

                {/* Date Range Filter Section */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h4 className="font-semibold mb-3 text-gray-800">Date Range Filter</h4>
                  <div className="flex flex-col lg:flex-row gap-4 items-center">
                    {/* Date Inputs */}
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                      <label className="text-sm font-medium text-gray-600">From:</label>
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                      <label className="text-sm font-medium text-gray-600">To:</label>
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Apply Filter Button */}
                    <button
                      onClick={() => {
                        const days = Math.ceil(
                          (new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (1000 * 3600 * 24)
                        );
                        setActivePreset(null);
                        fetchJournalData(null, null, days > 365);
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Loading...' : 'Apply Filter'}
                    </button>
                  </div>
                  
                  {/* Quick Preset Buttons */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-sm font-medium text-gray-600 mr-2">Quick Select:</span>
                    {[
                      { label: 'Last 7 days',   days: 7,    expandRange: false },
                      { label: 'Last 30 days',  days: 30,   expandRange: false },
                      { label: 'Last 90 days',  days: 90,   expandRange: false },
                      { label: 'Last 6 months', days: 180,  expandRange: false },
                      { label: 'Last year',     days: 365,  expandRange: false },
                      { label: 'Entire history',days: 3650, expandRange: true  },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => {
                          const endDate = new Date();
                          const startDate = new Date();
                          startDate.setDate(startDate.getDate() - preset.days);
                          const startStr = startDate.toISOString().split('T')[0];
                          const endStr   = endDate.toISOString().split('T')[0];
                          setDateRange({ startDate: startStr, endDate: endStr });
                          setActivePreset(preset.label);
                          fetchJournalData(startStr, endStr, preset.expandRange);
                        }}
                        className={`px-3 py-1 text-xs rounded-md focus:outline-none focus:ring-1 font-medium ${
                          activePreset === preset.label
                            ? 'bg-blue-500 text-white ring-1 ring-blue-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Loading Indicator */}
                {loading && (
                  <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
                    {error}
                  </div>
                )}                {/* Dashboard Tab Content */}
                {activeTab === 'dashboard' && dashboardData && !loading && (
                  <div className="space-y-6">

                    {/* Current Date Range Display */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-blue-800 font-medium">
                          Showing data from {new Date(dateRange.startDate).toLocaleDateString()} to {new Date(dateRange.endDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>{/* Key Performance Indicators Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white">
                        <div className="text-sm opacity-90">Net P&L</div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(dashboardData.total_profit)}
                        </div>
                        <div className="text-xs opacity-75 mt-1">
                          {dashboardData.total_profit >= 0 ? '↗' : '↘'} incl. comm. &amp; swap
                        </div>
                      </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
                        <div className="text-sm opacity-90">Win Rate</div>
                        <div className="text-2xl font-bold">{dashboardData.win_rate.toFixed(2)}%</div>
                        <div className="text-xs opacity-75 mt-1">
                          {dashboardData.winning_trades} / {dashboardData.total_trades} trades
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
                        <div className="text-sm opacity-90">Profit Factor</div>
                        <div className="text-2xl font-bold">
                          {dashboardData.profit_factor.toFixed(2)}
                        </div>
                        <div className="text-xs opacity-75 mt-1">
                          {dashboardData.profit_factor >= 1.5 ? 'Excellent' : dashboardData.profit_factor >= 1.0 ? 'Good' : 'Poor'}
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 rounded-lg text-white">
                        <div className="text-sm opacity-90">Max Drawdown</div>
                        <div className="text-2xl font-bold">
                          {dashboardData.max_drawdown_pct.toFixed(1)}%
                        </div>
                        <div className="text-xs opacity-75 mt-1">
                          {formatCurrency(dashboardData.max_drawdown)}
                        </div>
                      </div>
                    </div>

                    {/* Advanced Statistics Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Trading Statistics */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800 border-b pb-2">Trading Statistics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Trades:</span>
                            <span className="font-medium">{dashboardData.total_trades}</span>
                          </div>                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Winning Trades:</span>
                            <span className="font-medium text-green-600">
                              {dashboardData.winning_trades}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Losing Trades:</span>
                            <span className="font-medium text-red-600">
                              {dashboardData.losing_trades}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Largest Win:</span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(dashboardData.largest_win)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Largest Loss:</span>
                            <span className="font-medium text-red-600">
                              {formatCurrency(dashboardData.largest_loss)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Average Win:</span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(dashboardData.average_win)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Average Loss:</span>
                            <span className="font-medium text-red-600">
                              {formatCurrency(dashboardData.average_loss)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Risk Management */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800 border-b pb-2">Risk Management</h4>
                        <div className="space-y-3">                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Max Consecutive Wins:</span>
                            <span className="font-medium text-green-600">
                              {dashboardData.max_consecutive_wins}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Max Consecutive Losses:</span>
                            <span className="font-medium text-red-600">
                              {dashboardData.max_consecutive_losses}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Recovery Factor:</span>
                            <span className="font-medium">
                              {dashboardData.recovery_factor.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Expectancy:</span>
                            <span className="font-medium">
                              {formatCurrency(dashboardData.expectancy)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Sharpe Ratio:</span>
                            <span className="font-medium">
                              {dashboardData.sharpe_ratio.toFixed(2)}
                            </span>
                          </div>                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Calmar Ratio:</span>
                            <span className="font-medium">
                              {dashboardData.calmar_ratio.toFixed(2)}
                            </span>
                          </div>                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Avg Holding Time:</span>
                            <span className="font-medium">
                              {dashboardData.average_holding_time}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Account Information */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800 border-b pb-2">Account Information</h4>
                        <div className="space-y-3">                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Account Balance:</span>
                            <span className="font-medium">
                              {formatCurrency(dashboardData.current_balance)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Initial Deposit:</span>
                            <span className="font-medium">
                              {formatCurrency(dashboardData.starting_capital)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Account Growth:</span>
                            <span className={`font-medium ${dashboardData.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(((dashboardData.total_profit || 0) / dashboardData.starting_capital) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Monthly Return:</span>
                            <span className="font-medium">
                              {(((dashboardData.total_profit || 0) / 30 * 30 / dashboardData.starting_capital) * 100).toFixed(1)}%
                            </span>
                          </div>                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Commission:</span>
                            <span className="font-medium text-orange-600">
                              {formatCurrency(dashboardData.total_commission)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Swap:</span>
                            <span className="font-medium text-orange-600">
                              {formatCurrency(dashboardData.total_swap)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">                      {/* Balance & Equity Curve */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Balance & Equity Curve</h4>
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart
                            data={dashboardData.balance_curve || []}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" stroke="#666" fontSize={12} />
                            <YAxis stroke="#666" fontSize={12} />
                            <Tooltip 
                              formatter={(value, name, entry) => {
                                const key = String(entry?.dataKey || name || '').toLowerCase();
                                const label = key === 'balance' ? 'Balance' : key === 'equity' ? 'Equity' : String(name || 'Value');
                                return [formatCurrency(value), label];
                              }}
                              labelStyle={{color: '#666'}}
                              contentStyle={{backgroundColor: 'white', border: '1px solid #ccc'}}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="equity"
                              stroke="#10b981"
                              strokeWidth={3}
                              dot={false}
                              name="Equity"
                            />
                            <Line
                              type="monotone"
                              dataKey="balance"
                              stroke="#2563eb"
                              strokeWidth={3}
                              dot={{ r: 1 }}
                              activeDot={{ r: 4 }}
                              name="Balance"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Performance by Currency Pairs */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Performance by Currency Pairs</h4>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={dashboardData.pair_performance || []}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="pair" stroke="#666" fontSize={12} />
                            <YAxis stroke="#666" fontSize={12} />
                            <Tooltip 
                              formatter={(value) => [formatCurrency(value), 'Profit']}
                              labelStyle={{color: '#666'}}
                              contentStyle={{backgroundColor: 'white', border: '1px solid #ccc'}}
                            />
                            <Bar 
                              dataKey="profit" 
                              fill="#10b981"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bottom Row - Direction Analysis & Win/Loss Distribution */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">                      {/* Trade Direction Performance */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Long vs Short Performance</h4>
                        <div className="flex justify-center">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Long Trades', value: Math.abs(dashboardData.long_profit) },
                                  { name: 'Short Trades', value: Math.abs(dashboardData.short_profit) }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                              >
                                <Cell fill="#3b82f6" />
                                <Cell fill="#ef4444" />
                              </Pie>
                              <Tooltip 
                                formatter={(value) => [formatCurrency(value), '']}
                                contentStyle={{backgroundColor: 'white', border: '1px solid #ccc'}}
                              />
                              <Legend 
                                verticalAlign="bottom" 
                                height={36}
                                formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Win/Loss Distribution */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Win/Loss Distribution</h4>
                        <div className="flex justify-center">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { 
                                    name: 'Winning Trades', 
                                    value: dashboardData.winning_trades,
                                    color: '#10b981'
                                  },
                                  { 
                                    name: 'Losing Trades', 
                                    value: dashboardData.losing_trades,
                                    color: '#ef4444'
                                  }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                <Cell fill="#10b981" />
                                <Cell fill="#ef4444" />
                              </Pie>
                              <Tooltip 
                                formatter={(value) => [value, 'Trades']}
                                contentStyle={{backgroundColor: 'white', border: '1px solid #ccc'}}
                              />
                            </PieChart>
                          </ResponsiveContainer>                        </div>
                      </div>
                    </div>

                    {/* Additional Professional Metrics Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Drawdown Chart */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Drawdown Analysis</h4>
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Current Drawdown:</span>
                            <span className="font-medium text-red-600">
                              {(dashboardData.max_drawdown_pct / 2).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-red-500 h-2 rounded-full" 
                              style={{width: `${Math.min((dashboardData.max_drawdown_pct / 2) * 2, 100)}%`}}
                            ></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Max DD:</span>
                            <span className="ml-2 font-medium">{dashboardData.max_drawdown_pct.toFixed(1)}%</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Recovery Factor:</span>
                            <span className="ml-2 font-medium">{dashboardData.recovery_factor.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Performance Metrics Table */}
                      <div className="bg-white p-6 rounded-lg shadow-lg border">
                        <h4 className="font-semibold mb-4 text-gray-800">Performance Summary</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Total Return:</span>
                            <span className={`font-medium ${dashboardData.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(((dashboardData.total_profit || 0) / dashboardData.starting_capital) * 100).toFixed(2)}%
                            </span>
                          </div>                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Profit per Trade:</span>
                            <span className="font-medium">{formatCurrency(dashboardData.profit_per_trade || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Best Month:</span>
                            <span className="font-medium text-green-600">
                              {dashboardData.monthly_performance.length > 0 
                                ? formatCurrency(Math.max(...dashboardData.monthly_performance.map(m => m.profit)))
                                : formatCurrency(0)
                              }
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Worst Month:</span>
                            <span className="font-medium text-red-600">
                              {dashboardData.monthly_performance.length > 0 
                                ? formatCurrency(Math.min(...dashboardData.monthly_performance.map(m => m.profit)))
                                : formatCurrency(0)
                              }
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-sm text-gray-600">Best Pair:</span>
                            <span className="font-medium text-green-600">
                              {dashboardData.pair_performance.length > 0 
                                ? `${dashboardData.pair_performance[0].pair} (${formatCurrency(dashboardData.pair_performance[0].profit)})`
                                : 'N/A'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Journal Tab Content */}
                {activeTab === 'journal' && journalData && !loading && (
                  <div className="overflow-x-auto">
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => setJournalSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium flex items-center gap-1"
                      >
                        Sort by Date {journalSortOrder === 'asc' ? '↑ Oldest First' : '↓ Newest First'}
                      </button>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Pos ID
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Symbol
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th
                            className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => setJournalSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                          >
                            Open Time {journalSortOrder === 'asc' ? '↑' : '↓'}
                          </th>
                          <th
                            className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => setJournalSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                          >
                            Close Time {journalSortOrder === 'asc' ? '↑' : '↓'}
                          </th>
                          <th className="px-1 py-2 text-right font-medium text-gray-500 uppercase tracking-wider w-10">
                            Vol
                          </th>
                          <th className="px-1 py-2 text-right font-medium text-gray-500 uppercase tracking-wider w-16">
                            Open
                          </th>
                          <th className="px-1 py-2 text-right font-medium text-gray-500 uppercase tracking-wider w-16">
                            Close
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                            Comm.
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                            Swap
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                            Profit
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                            Net P&L
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                            Magic
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Comment
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {[...journalData.trades].sort((a, b) => {
                          const dateA = new Date(a.close_time || a.open_time || 0).getTime();
                          const dateB = new Date(b.close_time || b.open_time || 0).getTime();
                          return journalSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
                        }).map((trade, index) => (
                          <tr key={trade.position_id || trade.ticket || index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                              {trade.position_id || trade.ticket}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {trade.symbol}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`px-1.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                trade.type === 'buy' || trade.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {trade.type?.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {trade.open_time ? new Date(trade.open_time).toLocaleString() : 'N/A'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {trade.close_time ? new Date(trade.close_time).toLocaleString() : 'N/A'}
                            </td>
                            <td className="px-1 py-2 whitespace-nowrap text-right text-gray-600 w-10">
                              {trade.volume}
                            </td>
                            <td className="px-1 py-2 whitespace-nowrap text-right text-gray-600 w-16">
                              {trade.open_price}
                            </td>
                            <td className="px-1 py-2 whitespace-nowrap text-right text-gray-600 w-16">
                              {trade.close_price}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap text-right font-medium ${
                              (trade.commission || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(trade.commission || 0)}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap text-right font-medium ${
                              (trade.swap || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(trade.swap || 0)}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap text-right font-medium ${
                              trade.profit >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(trade.profit)}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap text-right font-bold ${
                              (trade.profit + (trade.commission || 0) + (trade.swap || 0)) >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {formatCurrency(trade.profit + (trade.commission || 0) + (trade.swap || 0))}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-gray-500 font-mono">
                              {trade.magic || 0}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-400 italic max-w-xs truncate" title={trade.comment || ''}>
                              {trade.comment || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                       <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                         {(() => {
                           const sorted = [...journalData.trades];
                           const totals = sorted.reduce((acc, t) => ({
                             commission: acc.commission + (t.commission || 0),
                             swap:       acc.swap       + (t.swap       || 0),
                             profit:     acc.profit     + (t.profit     || 0),
                           }), { commission: 0, swap: 0, profit: 0 });
                           const net = totals.profit + totals.commission + totals.swap;
                           return (
                             <tr className="font-semibold text-xs">
                               <td className="px-3 py-2 text-gray-700" colSpan={8}>
                                 Totals ({sorted.length} trades)
                               </td>
                               <td className={`px-3 py-2 text-right ${totals.commission >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                 {formatCurrency(totals.commission)}
                               </td>
                               <td className={`px-3 py-2 text-right ${totals.swap >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                 {formatCurrency(totals.swap)}
                               </td>
                               <td className={`px-3 py-2 text-right ${totals.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                 {formatCurrency(totals.profit)}
                               </td>
                               <td className={`px-3 py-2 text-right font-bold ${net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                 {formatCurrency(net)}
                               </td>
                               <td className="px-3 py-2" colSpan={2} />
                             </tr>
                           );
                         })()}
                       </tfoot>
                     </table>

                     {/* Deposits & Withdrawals Section */}
                     {(() => {
                       const cashflow = computeCashflowFromDeals(journalData.deals || []);
                       if (!cashflow.depositEntries.length && !cashflow.withdrawalEntries.length) return null;
                       return (
                         <div className="mt-6 border-t pt-4">
                           <h4 className="font-semibold text-gray-800 mb-3">Deposits & Withdrawals</h4>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                             <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                               <div className="text-sm text-gray-600 mb-1">Total Deposits</div>
                               <div className="text-xl font-bold text-green-700">{formatCurrency(cashflow.deposits)}</div>
                               {cashflow.lastDeposit && (
                                 <div className="text-xs text-gray-500 mt-1">
                                   Last: {new Date(cashflow.lastDeposit.time * 1000).toLocaleDateString()} &nbsp;+{formatCurrency(cashflow.lastDeposit.amount)}
                                 </div>
                               )}
                             </div>
                             <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                               <div className="text-sm text-gray-600 mb-1">Total Withdrawals</div>
                               <div className="text-xl font-bold text-red-700">{formatCurrency(Math.abs(cashflow.withdrawals))}</div>
                               {cashflow.lastWithdrawal && (
                                 <div className="text-xs text-gray-500 mt-1">
                                   Last: {new Date(cashflow.lastWithdrawal.time * 1000).toLocaleDateString()} &nbsp;{formatCurrency(cashflow.lastWithdrawal.amount)}
                                 </div>
                               )}
                             </div>
                           </div>
                           {/* Transaction log */}
                           <table className="min-w-full divide-y divide-gray-200 text-sm">
                             <thead className="bg-gray-50">
                               <tr>
                                 <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                 <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                 <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                               </tr>
                             </thead>
                             <tbody className="bg-white divide-y divide-gray-100">
                               {[...cashflow.depositEntries.map(e => ({ ...e, kind: 'deposit' })),
                                 ...cashflow.withdrawalEntries.map(e => ({ ...e, kind: 'withdrawal' }))]
                                 .sort((a, b) => b.time - a.time)
                                 .map((entry, i) => (
                                   <tr key={i}>
                                     <td className="px-4 py-2 text-gray-600">{new Date(entry.time * 1000).toLocaleDateString()}</td>
                                     <td className="px-4 py-2">
                                       <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${entry.kind === 'deposit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                         {entry.kind === 'deposit' ? 'Deposit' : 'Withdrawal'}
                                       </span>
                                     </td>
                                     <td className={`px-4 py-2 text-right font-mono font-medium ${entry.kind === 'deposit' ? 'text-green-700' : 'text-red-700'}`}>
                                       {entry.kind === 'deposit' ? '+' : ''}{formatCurrency(entry.amount)}
                                     </td>
                                   </tr>
                                 ))}
                             </tbody>
                           </table>
                         </div>
                       );
                     })()}
                   </div>
                 )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MT5HistoryIcon;